import { format, isToday, isYesterday } from "date-fns";

// ============================================================================
// Module: dayLabel
//
// 2026-09-14: the dashboard used to have exactly one date control, so every
// "today"/"reported today" string hardcoded into these single-day widgets
// (TopModelsUsed, TopProjects, NeedsAttentionCard, PeopleAndAgentsPreview,
// AgentsCostPanel) was true by construction — the picker only ever produced
// "today" as its implicit value. Once the shared control became a range
// picker with presets like "Yesterday"/"Last 7 days"/a custom past day, that
// stopped holding: those widgets still only ever show ONE day (range.to),
// but that day is often not actually today anymore, and the old copy kept
// insisting it was. These two helpers turn the `date` prop each of those
// widgets already receives into copy that's still accurate once it isn't
// today — a noun-phrase form for standalone labels ("Today" / "Sep 12,
// 2026") and an adverb form for embedding mid-sentence ("today" / "on Sep
// 12, 2026"), so "Blockers reported {adverb}" reads naturally either way.
// ============================================================================

function parseDayKey(dateKey: string): Date {
  // Matches the `date + 'T00:00:00'` local-midnight parsing pattern used
  // everywhere else in this codebase (NewDashboard.tsx, TokenSpendOverTime's
  // toDateKey, etc.) — parsing a bare "YYYY-MM-DD" as UTC would silently
  // shift it a day in negative-UTC-offset timezones.
  return new Date(dateKey + "T00:00:00");
}

/** "Today" | "Yesterday" | "Sep 12, 2026" — for standalone labels. */
export function dayLabel(dateKey: string): string {
  const d = parseDayKey(dateKey);
  if (isToday(d)) return "Today";
  if (isYesterday(d)) return "Yesterday";
  return format(d, "MMM d, yyyy");
}

/** "today" | "yesterday" | "on Sep 12, 2026" — for embedding mid-sentence. */
export function dayLabelAsOf(dateKey: string): string {
  const d = parseDayKey(dateKey);
  if (isToday(d)) return "today";
  if (isYesterday(d)) return "yesterday";
  return `on ${format(d, "MMM d, yyyy")}`;
}

// 2026-09-17: TopModelsUsed/TopProjects/AgentsCostPanel/NeedsAttentionCard/
// PeopleAndAgentsPreview stopped being single-day-only widgets — they now
// read the dashboard's full {from, to} range instead of just its end date
// (see NewDashboard.tsx). These two mirror dayLabel/dayLabelAsOf but collapse
// to the plain single-day form when from === to, so a "Today"/"Yesterday"
// pick still reads exactly as it always did — only an actual multi-day
// selection gets the range phrasing.

/** "Today" | "Yesterday" | "Sep 12, 2026" | "Sep 10 – Sep 17, 2026" — standalone label. */
export function rangeLabel(fromKey: string, toKey: string): string {
  if (fromKey === toKey) return dayLabel(fromKey);
  const from = parseDayKey(fromKey);
  const to = parseDayKey(toKey);
  const sameYear = from.getFullYear() === to.getFullYear();
  return `${format(from, sameYear ? "MMM d" : "MMM d, yyyy")} – ${format(to, "MMM d, yyyy")}`;
}

/** "today" | "yesterday" | "on Sep 12, 2026" | "between Sep 10 and Sep 17, 2026" — mid-sentence form. */
export function rangeLabelAsOf(fromKey: string, toKey: string): string {
  if (fromKey === toKey) return dayLabelAsOf(fromKey);
  const from = parseDayKey(fromKey);
  const to = parseDayKey(toKey);
  const sameYear = from.getFullYear() === to.getFullYear();
  return `between ${format(from, sameYear ? "MMM d" : "MMM d, yyyy")} and ${format(to, "MMM d, yyyy")}`;
}
